import React, { useEffect, useState, useRef } from 'react';
import { MotionConfig } from 'framer-motion';
import { useAppStore } from './app/hooks/useAppStore';
import { useAssessmentSession } from './assessment/hooks/useAssessmentSession';
import { useAppNavigation } from './app/hooks/useAppNavigation';
import { usePracticeSession } from './practice';
import { getAllQuestions, ensureAllContentLoaded, isContentLoaded, type Question } from './content';
import { isSessionExpired } from './accounts';
import { SUPABASE_AUTH_ENABLED, getSessionState, initSessionContext, subscribeToSession, applyProfileToAccounts, fetchLinkedPeople, type SessionState } from './data/supabase/session';
import { useStore } from './app/store';
import { deleteAssessmentDataForLearner } from './assessment/utils/assessmentStorage';
import { fetchSoloProgress, pushSoloProgress, mergeRemoteIntoLocal, resetSyncRevision } from './data/supabase/soloProgressSync';
import { fetchProgressForLearners } from './data/supabase/linkedProgress';
import { createResetState } from './config/initialState';
import { AuthGuard } from './app/AuthGuard';
import { AppContent } from './app/AppContent';
import { assertSupportCoverage } from './support';
import { warn, error as logError } from './utils/logger';
import { SESSION_CHECK_INTERVAL_MS } from './config/constants';
import { dataLayer } from './data/dataLayer';
import { loadV11ConfigOverrides } from './mastery/selection/v11ConfigStore';
// Apply V11_CONFIG runtime overrides as early as possible (P-14).
// Fire-and-forget: failures fall back to defaults.
loadV11ConfigOverrides().catch(() => {
  /* non-fatal */
});
export function App() {
  // ── Supabase-sessie ────────────────────────────────────────────────
  // Herstelt de sessie na een refresh en projecteert het profiel — inclusief
  // de ROL uit `public.profiles` — als ACTIEF account. Zonder dit blijft een
  // oud localStorage-account actief terwijl je als iemand anders bent
  // ingelogd, en ben je na elke refresh uitgelogd.
  const [session, setSession] = useState<SessionState>(() => SUPABASE_AUTH_ENABLED ? getSessionState() : {
    status: 'anonymous'
  } as SessionState);
  useEffect(() => {
    if (!SUPABASE_AUTH_ENABLED) return;
    initSessionContext();
    return subscribeToSession(setSession);
  }, []);
  // Opruimplicht bij ingetrokken toestemming.
  //
  // De server wist zijn kopie (0010), maar de lokale kopie op dit apparaat
  // blijft anders staan — en zou bij de eerstvolgende sync weer geüpload
  // worden. Dan is de intrekking ongedaan gemaakt door een achtergrondproces.
  // Dit draait bij elke sessiewijziging én bij het opstarten, zodat een
  // apparaat dat offline was alsnog opruimt zodra het terugkomt.
  useEffect(() => {
    if (!SUPABASE_AUTH_ENABLED) return;
    if (session.status !== 'authenticated') return;
    if (session.profile.consentStatus !== 'withdrawn') return;
    // LEERLING-GESCOPED, niet alles. `clearAllAssessmentData()` stond hier
    // eerst, en dat wiste op een gedeeld gezinsapparaat ook de toetsgegevens
    // van een broertje of zusje — een kind bestraft voor een beslissing van de
    // ouder van een ánder kind.
    void deleteAssessmentDataForLearner(session.profile.id).catch(err => logError('[Consent] Lokale toetsdata wissen mislukt', err));
  }, [session]);
  useEffect(() => {
    if (!SUPABASE_AUTH_ENABLED) return;
    if (session.status !== 'authenticated') return;
    const s = useStore.getState();
    const accts = s.multiAccountState?.accounts;
    if (!accts) return;
    // Al actief én INHOUDELIJK gelijk? Niets doen — anders schrijven we bij
    // elke tokenvernieuwing een nieuwe `lastActive` weg en ontstaat een
    // renderlus.
    //
    // De vergelijking moet wel de VELDEN meenemen, niet alleen het id. Deed
    // hij dat niet, dan bleef een eerder geprojecteerd account voor altijd
    // staan zoals het toen was — en een veld dat later aan de projectie wordt
    // toegevoegd (zoals de koppelcode) verscheen nooit bij bestaande
    // gebruikers.
    const existing = accts.accountsById[session.profile.id] as Record<string, unknown> | undefined;
    if (accts.activeAccountId === session.profile.id && existing && existing.role === session.profile.role && existing.displayName === (session.profile.displayName ?? 'Gebruiker') && (existing.linkCode ?? null) === session.profile.linkCode && (existing.username ?? null) === session.profile.username) {
      return;
    }
    useStore.setState({
      multiAccountState: {
        ...s.multiAccountState,
        accounts: applyProfileToAccounts(accts as never, session.profile) as never
      }
    });
  }, [session]);

  // ── Gekoppelde kinderen ophalen (ouderportaal) ─────────────────────
  // De koppeling staat op de server in `parent_links`, maar het portaal leest
  // zijn kinderen uit de lokale accountstaat. Zonder deze projectie ziet een
  // ouder na het koppelen een leeg overzicht.
  useEffect(() => {
    if (!SUPABASE_AUTH_ENABLED) return;
    if (session.status !== 'authenticated') return;
    if (session.profile.role !== 'parent') return;
    const parentId = session.profile.id;
    void (async () => {
      const children = (await fetchLinkedPeople()).filter(p => p.relation === 'child').map(p => ({
        id: p.id,
        displayName: p.name,
        username: null as string | null,
        grade: null as number | null,
        linkCode: null as string | null
      }));
      if (children.length === 0) return;
      const s = useStore.getState();
      const accts = s.multiAccountState?.accounts;
      if (!s.multiAccountState || !accts) return;

      // Rechtstreeks opbouwen in plaats van via `applyProfileToAccounts`:
      // die functie zet elk profiel als ACTIEF account, en voor kinderen klopt
      // dat niet — de ouder blijft ingelogd. Dat achteraf terugdraaien werkte
      // wel, maar was niet na te lezen.
      const now = Date.now();
      const byId: Record<string, unknown> = {
        ...accts.accountsById
      };
      for (const child of children) {
        byId[child.id] = {
          ...(byId[child.id] as object ?? {}),
          id: child.id,
          role: 'learner',
          displayName: child.displayName ?? 'Leerling',
          username: child.username ?? child.id,
          grade: child.grade ?? undefined,
          linkCode: child.linkCode ?? undefined,
          createdAt: now,
          lastActive: now,
          hasCompletedOnboarding: true
        };
      }
      const rel = accts.relationships ?? {};
      useStore.setState({
        multiAccountState: {
          ...s.multiAccountState,
          accounts: {
            ...accts,
            accountsById: byId,
            activeAccountId: parentId,
            relationships: {
              ...rel,
              parentToLearners: {
                ...(rel.parentToLearners ?? {}),
                [parentId]: children.map(c => c.id)
              },
              learnerToParents: {
                ...(rel.learnerToParents ?? {}),
                ...Object.fromEntries(children.map(c => [c.id, [parentId]]))
              },
              teacherToLearners: rel.teacherToLearners ?? {}
            }
          }
        }
      });

      // ── En dan de voortgang zelf ──────────────────────────────────
      // Hierboven is alleen bekend geworden DAT deze kinderen bestaan. Hun
      // mastery, XP en streak staan op de server; zonder deze stap toont het
      // ouderportaal een kind met een lege voortgang, wat er uitziet als een
      // kind dat nooit heeft geoefend.
      //
      // Bewust ná de accountprojectie en in een eigen stap: mislukt dit door
      // een netwerkfout, dan ziet de ouder zijn kinderen nog wél staan.
      const progressByLearner = await fetchProgressForLearners(children.map(c => c.id));
      if (Object.keys(progressByLearner).length === 0) return;
      const s2 = useStore.getState();
      if (!s2.multiAccountState) return;
      const learnerData = {
        ...(s2.multiAccountState.learnerData ?? {})
      };
      for (const child of children) {
        const remote = progressByLearner[child.id];
        if (!remote) continue;
        // Dezelfde samenvoeging als bij het kind zelf, zodat er één definitie
        // is van "wat komt van de server". `createResetState` levert de lege
        // basis: op het apparaat van een ouder bestaat er nooit een lokale
        // staat voor dit kind.
        const base = learnerData[child.id] ?? createResetState(child.displayName ?? 'Leerling');
        learnerData[child.id] = mergeRemoteIntoLocal(base as never, remote) as never;
      }
      useStore.setState({
        multiAccountState: {
          ...s2.multiAccountState,
          learnerData
        }
      });
    })();
  }, [session]);

  // ── Voortgang synchroniseren (solo-accounts) ───────────────────────
  // Ophalen bij het inloggen. Alleen voor leerlingen: ouders en leerkrachten
  // hebben geen eigen voortgang.
  const pulledForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!SUPABASE_AUTH_ENABLED) return;
    // Uitloggen (of een haperende sessie) wist de markering én de bekende
    // revisie. Zonder dit bleef de markering op het vorige account staan:
    // logde hetzelfde kind opnieuw in — op een gedeeld klaslokaal-apparaat
    // eerder regel dan uitzondering — dan werd de serverstand NIET opnieuw
    // opgehaald en kon de app verouderde voortgang over een nieuwere versie
    // van een ander apparaat heen schrijven.
    if (session.status !== 'authenticated') {
      pulledForRef.current = null;
      resetSyncRevision();
      return;
    }
    const profile = session.profile;
    if (profile.role !== 'learner') return;
    if (pulledForRef.current === profile.id) return;
    // Ander account dan de vorige keer: de revisie van het vorige kind mag
    // niet meeliften naar deze sessie.
    if (pulledForRef.current !== null) resetSyncRevision();
    pulledForRef.current = profile.id;
    void (async () => {
      const remote = await fetchSoloProgress();
      if (!remote) return;
      const s = useStore.getState();
      if (!s.multiAccountState) return;
      // Op een VERS apparaat bestaat er nog geen lokale leerlingstaat — die
      // wordt pas bij de eerste wijziging aangemaakt. Hier stoppen was precies
      // de bug: juist op zo'n apparaat moet de serverdata landen. We maken dus
      // een lege staat en voegen de serverdata daarin samen.
      const local = s.multiAccountState.learnerData?.[profile.id] ?? createResetState(profile.displayName ?? 'Leerling');
      useStore.setState({
        multiAccountState: {
          ...s.multiAccountState,
          learnerData: {
            ...s.multiAccountState.learnerData,
            [profile.id]: mergeRemoteIntoLocal(local as never, remote) as never
          }
        }
      });
    })();
  }, [session]);
  const store = useAppStore();
  const {
    state,
    accounts,
    activeAccount,
    selectedGroup,
    startingPeriod,
    manualPeriod,
    setStartingPeriod,
    recordAnswer,
    markCheatSheetViewed,
    clearPendingLevelUp,
    updateProfileDisplayName,
    switchAccount,
    isInitialized,
    loadDemoAccounts,
    currentLearnerDisplayName,
    requiresViewingLearnerSelection,
    getLearnerData,
    getLearnerModuleMastery,
    viewingLearnerId,
    setViewingLearner
  } = store;

  // Voortgang wegschrijven na wijzigingen, met een rustpauze van 5 seconden.
  //
  // Bewust NIET bij elk antwoord: een kind beantwoordt in een oefensessie
  // tientallen vragen per minuut, en dat zou net zoveel netwerkverzoeken geven.
  // Wachten tot het even stil is levert één schrijfactie per opgavenreeks.
  //
  // Werkt ook offline zonder speciale afhandeling: mislukt de verzending, dan
  // staat de voortgang nog gewoon lokaal en gaat de volgende poging mee.
  useEffect(() => {
    if (!SUPABASE_AUTH_ENABLED) return;
    if (session.status !== 'authenticated') return;
    const profile = session.profile;
    if (profile.role !== 'learner') return;
    if (pulledForRef.current !== profile.id) return; // eerst ophalen, dan pas sturen

    const timer = setTimeout(() => {
      const local = useStore.getState().multiAccountState?.learnerData?.[profile.id];
      if (!local) return;
      void pushSoloProgress(local as never).then(ok => {
        // Conflict: een ander apparaat was eerder. Opnieuw ophalen zodat de
        // volgende schrijfactie op de juiste revisie bouwt in plaats van
        // eindeloos te blijven botsen.
        if (!ok) void fetchSoloProgress();
      });
    }, 5000);
    return () => clearTimeout(timer);
  }, [session, state]);
  // ── Content System Initialization (non-blocking) ──────────────────
  // Boot Flow Refactor (mei 2026): content is loaded in the background
  // so pre-auth screens (Welcome / Login / Register) render instantly.
  // Assessment / practice gate themselves via `isReady`.
  //
  // We must use useState (not useRef) for the questions array so that
  // re-renders after async load / HMR / idle prefetch pick up the data.
  const [contentReady, setContentReady] = useState<boolean>(() => isContentLoaded());
  const [contentError, setContentError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<Question[]>(() => {
    if (!isContentLoaded()) return [];
    try {
      return getAllQuestions();
    } catch {
      return [];
    }
  });
  const nav = useAppNavigation({
    accounts,
    activeAccount,
    isInitialized,
    switchAccount,
    setViewingLearner,
    loadDemoAccounts,
    clearActiveAccount: store.clearActiveAccount
  });
  // Trigger content load once the user passes the welcome screen, OR via
  // idle-prefetch after first paint as a fallback for offline parity.
  //
  // Boot Flow Refactor v2 (mei 2026): the previous implementation used a
  // 500ms `setInterval` to poll `isContentLoaded()` during pre-auth — a
  // wasteful tick loop on every browser tab. Replaced with a single
  // idle-scheduled `ensureAllContentLoaded().then(finishLoad)` chain that
  // resolves directly, no polling. `ensureAllContentLoaded` is idempotent
  // (it caches its promise), so a later `load()` call after the user
  // picks a path just awaits the same in-flight promise.
  useEffect(() => {
    if (contentReady) return;
    let cancelled = false;
    const finishLoad = () => {
      try {
        const q = getAllQuestions();
        if (q.length === 0) warn('No questions loaded - check question data files');
        if (import.meta.env?.DEV) assertSupportCoverage(q);
        setQuestions(q);
      } catch (err) {
        logError('Failed to process questions:', err);
        setQuestions([]);
      }
      setContentReady(true);
    };
    const load = () => {
      ensureAllContentLoaded().then(() => {
        if (cancelled) return;
        finishLoad();
      }).catch(err => {
        if (cancelled) return;
        logError('Content system initialization failed:', err);
        setContentError('Kon leerinhoud niet laden. Probeer de pagina te vernieuwen.');
      });
    };
    if (!nav.showWelcome) {
      // User has chosen a path (demo / login / register-success) — load now.
      load();
      return () => {
        cancelled = true;
      };
    }
    // Pre-auth: schedule a load during browser idle so the cache is warm
    // by the time the user picks a path. Single awaited promise, no polling.
    const ric = (window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: {
        timeout: number;
      }) => number;
      cancelIdleCallback?: (id: number) => void;
    }).requestIdleCallback;
    let idleHandle: number | null = null;
    let timeoutHandle: number | null = null;
    if (ric) {
      idleHandle = ric(() => {
        if (cancelled) return;
        load();
      }, {
        timeout: 2000
      });
    } else {
      timeoutHandle = window.setTimeout(() => {
        if (cancelled) return;
        load();
      }, 100);
    }
    return () => {
      cancelled = true;
      if (idleHandle !== null && (window as unknown as {
        cancelIdleCallback?: (id: number) => void;
      }).cancelIdleCallback) {
        ;
        (window as unknown as {
          cancelIdleCallback: (id: number) => void;
        }).cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== null) window.clearTimeout(timeoutHandle);
    };
  }, [nav.showWelcome, contentReady]);
  const QUESTIONS = questions;
  // Load reducedMotion preference from dataLayer (D5)
  const [reducedMotionPref, setReducedMotionPref] = useState<boolean>(false);
  useEffect(() => {
    let cancelled = false;
    dataLayer.getLearnerPreferences().then(prefs => {
      if (!cancelled) setReducedMotionPref(prefs.reducedMotion);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const practice = usePracticeSession({
    state,
    selectedGroup,
    markCheatSheetViewed,
    enterPracticeView: nav.enterPracticeView,
    resetToDashboard: nav.resetToDashboard,
    resetToLearningLab: nav.resetToLearningLab,
    onAssignmentComplete: (assignmentId: string) => {
      // BUG-HW-3 FIX: Mark teacher PracticeAssignment as completed so the
      // banner disappears on the next HomePage load.
      const learnerId = store.viewingLearnerId || (activeAccount?.role === 'learner' ? activeAccount?.id : null);
      if (!learnerId) return;
      dataLayer.markPracticeAssignmentCompletedByStudent(assignmentId, learnerId).catch(e => logError('Failed to mark practice assignment completed', e));
    }
  });
  const assessment = useAssessmentSession({
    familyMastery: state.familyMastery || {},
    selectedGroup,
    recordAnswer,
    allQuestions: QUESTIONS,
    isReady: contentReady && isInitialized && !nav.showWelcome,
    learnerId: viewingLearnerId || activeAccount?.id
  });
  // ── Session Timeout Enforcement (Spec 8.3) ─────────────────────────
  const activeAccountRef = useRef(activeAccount);
  activeAccountRef.current = activeAccount;
  useEffect(() => {
    if (!isInitialized || nav.showWelcome) return;
    if (nav.currentNavView === 'login' || nav.currentNavView === 'register') return;
    // Met Supabase-auth bewaakt de server de geldigheid en vernieuwt de client
    // het token automatisch. Deze lokale check zou iemand uitloggen op basis
    // van een verouderde `lastActive` terwijl zijn token nog prima geldig is —
    // halverwege een toets funest.
    if (SUPABASE_AUTH_ENABLED) return;
    const checkSessionTimeout = () => {
      const acct = activeAccountRef.current;
      if (acct && acct.lastActive) {
        const expired = isSessionExpired(acct.lastActive, acct.role);
        if (expired) nav.handleLogout();
      }
    };
    checkSessionTimeout();
    const interval = setInterval(checkSessionTimeout, SESSION_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isInitialized, activeAccount?.id, activeAccount?.role, nav.showWelcome, nav.currentNavView]);
  // ── Assessment handlers ──────────────────────────────────────────
  const handleStartAssessment = (config: import('./assessment/types').AssessmentConfig) => {
    const result = assessment.startAssessment(config);
    if (result) nav.setCurrentView('assessment-session');
  };
  /**
   * AS-2026-07-P1-03 (July 2026): assignment completion is now gated on
   * DURABLE persistence of the attempt + report.
   *
   * Previously this fired `completeAssessment()` without awaiting it and
   * marked the assignment complete unconditionally. On a storage failure the
   * learner saw an error, no attempt or report existed on disk, and yet the
   * teacher's ledger said "completed" — the assignment disappeared from the
   * learner's list with nothing to show for it.
   *
   * Order now: persist → verify → mark ledger → navigate. On failure the
   * assignment deliberately stays active so the learner can retry, and the
   * hook keeps the in-memory report on screen (see `completeAssessment`).
   */
  const handleAssessmentComplete = async () => {
    // Capture assignmentId before completeAssessment clears the state
    const assignmentId = assessment.assessmentAttempt?.assignmentId;
    const learnerId = store.viewingLearnerId || (activeAccount?.role === 'learner' ? activeAccount?.id : null);
    const result = await assessment.completeAssessment();
    if (result.persisted && assignmentId && learnerId) {
      try {
        const marked = await dataLayer.markAssignmentCompletedByStudent(assignmentId, learnerId);
        if (!marked) {
          logError(`[Assessment] Assignment ${assignmentId} could not be marked completed for learner ${learnerId} — ledger write did not persist. The assignment stays active.`);
        }
      } catch (e) {
        logError('Failed to mark assignment completed', e);
      }
    } else if (!result.persisted && assignmentId) {
      logError(`[Assessment] Skipped completion-marking for assignment ${assignmentId}: results did not persist (${result.reason}). Assignment remains active so the learner can retry.`);
    }

    // Navigate regardless: on failure the hook has surfaced an error and kept
    // the report in memory, which the learner still needs to be able to read.
    nav.resetToDashboard();
    nav.setCurrentNavView('assessmentReport');
  };
  const handleResumeAssessment = () => {
    const result = assessment.resumeAssessment();
    if (result) nav.setCurrentView('assessment-session');
  };
  // ── Render ─────────────────────────────────────────────────────────
  // Content load failure: dedicated error screen.
  if (contentError) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center" role="alert" data-id="element-556" data-mp="28hhfwinyitur">
        <div className="text-center space-y-4 max-w-sm px-4" data-id="element-557" data-mp="1ylefbh8r739x">
          <div className="text-3xl" data-id="element-558" data-mp="1ylxwuwuhrpnf">⚠️</div>
          <p className="text-slate-700 font-medium" data-id="element-559" data-mp="2maqle88sav80">{contentError}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors" data-id="element-560" data-mp="2mdl5fynvye0w">
            Pagina vernieuwen
          </button>
        </div>
      </div>;
  }
  return <MotionConfig reducedMotion={reducedMotionPref ? 'always' : 'user'} data-id="element-561" data-mp="2hi5w9k0tp6at">
      <AuthGuard showWelcome={nav.showWelcome} currentNavView={nav.currentNavView} accounts={accounts} activeAccount={activeAccount} onLoadDemo={nav.handleLoadDemoAccounts} isLoadingDemo={nav.isLoadingDemo} onNavigateToLogin={nav.handleNavigateToLogin} onNavigateToRegister={nav.handleNavigateToRegister} onLoginSuccess={nav.handleLoginSuccess} onBack={() => nav.setShowWelcome(true)} createAccount={store.createAccount} onCompleteOnboarding={stepState => store.completeOnboarding(undefined, stepState)} data-id="element-562" data-mp="358tgn4rxdoe9">
        <AppContent questions={QUESTIONS} state={state} accounts={accounts} activeAccount={activeAccount} selectedGroup={selectedGroup} startingPeriod={startingPeriod} manualPeriod={manualPeriod} setStartingPeriod={setStartingPeriod} recordAnswer={recordAnswer} markCheatSheetViewed={markCheatSheetViewed} clearPendingLevelUp={clearPendingLevelUp} updateProfileDisplayName={updateProfileDisplayName} currentLearnerDisplayName={currentLearnerDisplayName} requiresViewingLearnerSelection={requiresViewingLearnerSelection} getLearnerData={getLearnerData} getLearnerModuleMastery={getLearnerModuleMastery} viewingLearnerId={viewingLearnerId} nav={nav} practice={practice} assessment={assessment} handleStartAssessment={handleStartAssessment} handleAssessmentComplete={handleAssessmentComplete} handleResumeAssessment={handleResumeAssessment} contentReady={contentReady} data-id="element-563" data-mp="anwuopkxghoj" />
      </AuthGuard>
    </MotionConfig>;
}